import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import { activityApi } from "../../services/api";

const CATEGORY_ICONS = {
  "Heli Tour": "Helicopter",
  "Scenic Flight": "Plane",
  "Adventure Sports": "Zap",
  Wildlife: "Binoculars",
  "Cultural Tour": "Landmark",
  "Festival Tour": "Music",
  Wellness: "Heart",
};

const CATEGORY_COLORS = {
  "Heli Tour": "bg-amber-100 text-amber-700 border-amber-200",
  "Scenic Flight": "bg-sky-100 text-sky-700 border-sky-200",
  "Adventure Sports": "bg-orange-100 text-orange-700 border-orange-200",
  Wildlife: "bg-green-100 text-green-700 border-green-200",
  "Cultural Tour": "bg-purple-100 text-purple-700 border-purple-200",
  "Festival Tour": "bg-pink-100 text-pink-700 border-pink-200",
  Wellness: "bg-teal-100 text-teal-700 border-teal-200",
};

const ACTIVITY_IMAGES = {
  "trek-cooking-class": "https://upload.wikimedia.org/wikipedia/commons/6/60/Nepali_dal-bhat-tarkari.jpg",
  "world-heritage-sites": "https://upload.wikimedia.org/wikipedia/commons/1/17/Patan_Durbar_Square-2644.jpg",
  "lumbini-pilgrim-tour": "https://upload.wikimedia.org/wikipedia/commons/e/e5/MayaDevi_Temple%2C_Lumbini.jpg",
  "jungle-safari": "https://upload.wikimedia.org/wikipedia/commons/6/61/Elephant_Safari_inside_Chitwan_National_Park.jpg",
  "rafting": "https://upload.wikimedia.org/wikipedia/commons/5/54/Trishuli_River_Rafting%2C_Nepal-3133.jpg",
  "heli-tour-everest-base-camp": "https://upload.wikimedia.org/wikipedia/commons/d/d0/Private_helicopter_near_Nilgiri_mountain.jpg",
  "mountain-flight": "https://upload.wikimedia.org/wikipedia/commons/d/d7/MOUNT_EVEREST_FROM_SIMRIK_AIR_FLIGHT_SMA301_9N-AGL_FLYING_EAST_FROM_KATHMANDU_AT_21000_FEET_NEPAL_FEB2013_%288497707390%29.jpg",
  "yoga-retreat-nepal": "https://media.istockphoto.com/id/117943067/photo/yoga-in-himalayas.jpg?s=612x612&w=0&k=20&c=y02F7XP5pslpGydepiAJplLx0A6hiPeXFQ3N1PJdsNU=",
  "ayurveda-package": "https://t3.ftcdn.net/jpg/00/71/49/40/1000_F_71494035_yrgVSCBDEN3TqDd9Tk9jzUi6q8rbzSXn.jpg",
  "dashain-tihar-festival": "https://imgs.search.brave.com/XvNhQQQNMSGEr5CFvmkossD7XAXGU0YDVvo-ezj9P4g/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly9tZWRp/YS5nZXR0eWltYWdl/cy5jb20vaWQvMjE4/MTQwMjk5Mi9waG90/by9naXJscy1tYWtl/LXJhbmdvbGktaW4t/ZnJvbnQtb2YtdGhl/aXItaG91c2UtZHVy/aW5nLWxheG1pLXB1/amEtYXMtcGFydC1v/Zi10aGUtdGloYXIt/ZmVzdGl2YWwuanBn/P3M9NjEyeDYxMiZ3/PTAmaz0yMCZjPXpH/bnpqSEwwVjZlLU1M/UEZ4TjRnNmtIOVFr/X1JSUEJGb2xHTTB2/YTRTTE09",
  "holi-festival": "https://imgs.search.brave.com/c-TTnB-0XKDSlwKhZqDgT83g9cIw07Fomoay8r3ojNc/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly93d3cu/YWxqYXplZXJhLmNv/bS93cC1jb250ZW50/L3VwbG9hZHMvMjAy/NS8wMy9BUDI1MDcy/MjczOTE0MzE1LTE3/NDE5MzYyNjguanBn/P2ZpdD0xMTcwLDc4/MCZxdWFsaXR5PTgw",
  "loshar-festival": "https://imgs.search.brave.com/P0DUZ1xaiQsAiJsdpP79Eluc4_yv8-sJK4eXkrNAC6g/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly9ibG9u/aG9saWRheS5maWxl/cy53b3JkcHJlc3Mu/Y29tLzIwMjAvMDQv/dGFtYW5nLXNoYW1h/bnMtaW4tdHJhZGl0/aW9uYWwtYXR0aXJl/LXBsYXktZHJ1bXMt/ZHVyaW5nLXNvbmFt/LWxob3Nhci1mZXN0/aXZhbC1pbi1rYXRo/bWFuZHUuanBnP3c9/ODAw",
  "teej-festival": "https://upload.wikimedia.org/wikipedia/commons/b/b7/Teej_in_Nepal_2010.jpg",
  "ropai-festival": "https://upload.wikimedia.org/wikipedia/commons/5/5b/Planting_Paddy_Nepal.jpg",
};

const ActivitiesHub = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(searchParams.get("category") || "all");

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true);
        const data = await activityApi.getAllActivities();
        setActivities(data);
      } catch (err) {
        setError(err.message || "Failed to load activities");
      } finally {
        setLoading(false);
      }
    };
    fetchActivities();
  }, []);

  const categories = useMemo(() => {
    const cats = [...new Set(activities.map((a) => a.activityData?.category || a.category))];
    return ["all", ...cats.sort()];
  }, [activities]);

  const filtered = useMemo(() => {
    if (activeCategory === "all") return activities;
    return activities.filter(
      (a) => (a.activityData?.category || a.category) === activeCategory,
    );
  }, [activities, activeCategory]);

  const heroBg = "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1600&h=600&fit=crop";

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* Hero */}
        <section
          className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroBg})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/50" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 text-primary-light mb-4">
                <Icon name="ChefHat" size={18} />
                <span className="text-sm font-medium uppercase tracking-wider text-amber-400">
                  Nepali Food Cooking
                </span>
              </div>
              <h1 className="section-heading text-white mb-6">
                Activities & Experiences
              </h1>
              <p className="text-lg text-white/80 leading-relaxed mb-8">
                From helicopter tours over Everest to cultural festivals, wellness retreats, 
                and jungle safaris — discover the full spectrum of adventures Nepal has to offer.
              </p>
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="sticky top-[105px] z-30 bg-white/95 backdrop-blur-md border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 py-4 overflow-x-auto scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 border ${
                    activeCategory === cat
                      ? "bg-accent text-white border-accent shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:border-accent hover:text-accent"
                  }`}
                >
                  {cat !== "all" && CATEGORY_ICONS[cat] && (
                    <Icon name={CATEGORY_ICONS[cat]} size={14} />
                  )}
                  {cat === "all" ? (
                    <>
                      <Icon name="Layers" size={14} />
                      All Activities
                    </>
                  ) : (
                    cat
                  )}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Activities Grid */}
        <section className="py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <LoadingSpinner text="Loading activities..." />
            ) : error ? (
              <div className="text-center py-16">
                <Icon name="AlertCircle" size={48} className="mx-auto text-red-400 mb-4" />
                <p className="text-gray-600">{error}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <Icon name="SearchX" size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-500 mb-2">No activities found</h3>
                <p className="text-gray-400">Try a different category filter.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {filtered.map((activity) => {
                  const data = activity.activityData || activity;
                  const cat = data.category || activity.category;
                  return (
                    <div
                      key={activity.id}
                      onClick={() => navigate(`/activities/${activity.id}`)}
                      className="group relative h-[420px] rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
                    >
                      <img
                        src={ACTIVITY_IMAGES[activity.id] || data.heroImage || "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=600&h=400&fit=crop"}
                        alt={data.name}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                      <div className="absolute top-4 left-4 right-4 flex items-start justify-between">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border shadow-sm ${CATEGORY_COLORS[cat] || "bg-white/90 text-gray-700 border-gray-200"}`}>
                          {CATEGORY_ICONS[cat] && <Icon name={CATEGORY_ICONS[cat]} size={11} />}
                          {cat}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-5 lg:p-6">
                        <h3 className="font-headline text-lg text-white mb-1.5 leading-tight" style={{ fontWeight: 300 }}>
                          {data.name}
                        </h3>
                        <p className="text-sm text-white/70 line-clamp-2 mb-3">
                          {data.subtitle || data.overview?.slice(0, 100)}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-white/60">
                          <span className="flex items-center gap-1.5">
                            <Icon name="Clock" size={13} />
                            {data.duration}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
            <div className="col-span-2">
              <a href="/" className="flex items-center gap-3 mb-5 group w-fit">
                <div className="relative">
                  <div className="w-11 h-11 rounded-xl overflow-hidden ring-2 ring-primary/30">
                    <img src="/assets/images/logo.jpeg" alt="Sherpa Trekker" className="w-full h-full object-cover" />
                  </div>
                </div>
                <div>
                  <span className="font-headline font-bold text-lg text-white block">Sherpa Trekker</span>
                </div>
              </a>
              <p className="text-white/65 text-sm leading-relaxed max-w-xs mb-6">
                Guided by generations of Sherpa wisdom, we create transformative Himalayan experiences.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-white/65">
                  <Icon name="Phone" size={14} className="text-primary" />
                  +977-9851157213
                </div>
                <div className="flex items-center gap-2 text-sm text-white/65">
                  <Icon name="Mail" size={14} className="text-primary" />
                  imsherpatrekker@gmail.com
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm mb-4">Activities</h4>
              <ul className="space-y-2.5">
                <li><a href="/activities?category=Heli+Tour" className="text-sm text-white/60 hover:text-white">Heli Tours</a></li>
                <li><a href="/activities?category=Wildlife" className="text-sm text-white/60 hover:text-white">Wildlife</a></li>
                <li><a href="/activities?category=Cultural+Tour" className="text-sm text-white/60 hover:text-white">Cultural Tours</a></li>
                <li><a href="/activities?category=Festival+Tour" className="text-sm text-white/60 hover:text-white">Festivals</a></li>
                <li><a href="/activities?category=Wellness" className="text-sm text-white/60 hover:text-white">Wellness</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm mb-4">Quick Links</h4>
              <ul className="space-y-2.5">
                <li><a href="/trek-discovery-hub" className="text-sm text-white/60 hover:text-white">Trek Discovery Hub</a></li>
                <li><a href="/sherpa-guide-profiles" className="text-sm text-white/60 hover:text-white">Our Guides</a></li>
                <li><a href="/traveler-stories-hub" className="text-sm text-white/60 hover:text-white">Traveler Stories</a></li>
                <li><a href="/booking-consultation-center" className="text-sm text-white/60 hover:text-white">Book Consultation</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-center text-sm text-white/50">
            &copy; {new Date().getFullYear()} Sherpa Trekker. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ActivitiesHub;
